import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import {
  deleteProductService,
  getAllProductsService,
  getProductByIdService,
} from "redux/services/products_sevice";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const ProductTable = ({ products, sortConfig, onSort }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [deleteProductId, setDeleteProductId] = useState(null);

  /* ================= Helpers ================= */

  const Badge = ({ color, children }) => (
    <span
      className={`
        text-center
        inline-flex items-center justify-center
        h-5 min-h-[20px]
        px-2
        text-[10px] sm:text-xs
        leading-none
        rounded-full
        whitespace-nowrap
        w-fit
        ${color}
      `}
    >
      {children}
    </span>
  );

  const getStockBadge = (qty) => {
    if (qty === 0)
      return (
        <Badge color="bg-red-500 text-white text-center">
          {t("outOfStock")}
        </Badge>
      );
    if (qty <= 10)
      return (
        <Badge color="bg-yellow-400 text-black text-center">
          {t("lowStock")}
        </Badge>
      );
    return (
      <Badge color="bg-green-600 text-white text-center">{t("inStock")}</Badge>
    );
  };

  const getTotalQuantity = (product) =>
    product.options?.reduce((sum, o) => sum + Number(o.quantity), 0) || 0;

  /* ================= Actions ================= */

  const handleEdit = async (product) => {
    await dispatch(getProductByIdService(product.id));
    navigate("/edit-product");
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteProductService(deleteProductId)).unwrap();
      toast.success(t("تم الحذف بنجاح"));
      dispatch(getAllProductsService({ page: 1 }));
      setDeleteProductId(null);
    } catch {
      toast.error(t("فشل في الحذف"));
    }
  };

  /* ================= Fields ================= */

  const fields = [
    {
      key: "product",
      label: t("product"),
      renderDesktop: (p) => (
        <div className="flex gap-3">
          <Image src={p.images?.[0]?.file_path} className="w-12 h-12 rounded" />
          <div>
            <div className="font-medium">{p.translations?.ar?.name}</div>
            <div className="text-sm text-muted-foreground">
              {p.translations?.ar?.desc}
            </div>
          </div>
        </div>
      ),
      renderMobile: (p) => (
        <div className="flex gap-3">
          <Image src={p.images?.[0]?.file_path} className="w-14 h-14 rounded" />
          <div className="font-medium">{p.translations?.ar?.name}</div>
        </div>
      ),
      primary: true,
    },
    {
      key: "category",
      label: t("category"),
      renderDesktop: (p) => (
        <div className="text-center">
          {p?.category?.parent_category !== null
            ? p?.category?.parent_category?.translations?.ar?.name
            : p.category?.translations?.ar?.name}
        </div>
      ),
      renderMobile: (p) => <div>{p.category?.translations?.ar?.name}</div>,
    },
    {
      key: "options",
      label: t("options"),
      renderDesktop: (p) =>
        p.options?.length ? (
          <div className="space-y-1 text-center">
            {p.options.map((o) => (
              <div key={o.id} className="text-sm">
                {o.weight_string} — {o.quantity}
              </div>
            ))}
          </div>
        ) : (
          "-"
        ),
      renderMobile: (p) =>
        p.options?.map((o) => (
          <div key={o.id}>
            {o.weight_string} — {o.quantity}
          </div>
        )),
    },
    {
      key: "status",
      label: t("status"),
      renderDesktop: (p) => getStockBadge(getTotalQuantity(p)),
      renderMobile: (p) => getStockBadge(getTotalQuantity(p)),
      header: true,
    },
  ];

  /* ================= Render ================= */

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50">
            <tr>
              {fields.map((f) => (
                <th
                  key={f.key}
                  onClick={() => onSort?.(f.key)}
                  className="p-4 cursor-pointer"
                >
                  {f.label}
                </th>
              ))}
              <th className="p-4 text-center">{t("actions")}</th>
            </tr>
          </thead>

          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b hover:bg-muted/40 text-center">
                {fields.map((f) => (
                  <td key={f.key} className="p-4">
                    {f.renderDesktop(p)}
                  </td>
                ))}

                <td className="p-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Icon name="Edit2" onClick={() => handleEdit(p)} />
                    <Icon
                      name="Trash2"
                      className="text-red-500"
                      onClick={() => setDeleteProductId(p.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y">
        {products.map((p) => (
          <div key={p.id} className="p-4 space-y-3">
            <div className="flex justify-between">
              {fields.find((f) => f.primary)?.renderMobile(p)}
              {fields.find((f) => f.header)?.renderMobile(p)}
            </div>

            {fields
              .filter((f) => !f.primary && !f.header)
              .map((f) => (
                <div key={f.key} className="text-sm">
                  <strong>{f.label}:</strong> {f.renderMobile(p)}
                </div>
              ))}

            <div className="flex justify-end gap-3">
              <Icon name="Edit2" onClick={() => handleEdit(p)} />
              <Icon
                name="Trash2"
                className="text-red-500"
                onClick={() => setDeleteProductId(p.id)}
              />
            </div>
          </div>
        ))}
      </div>

      {deleteProductId && (
        <DeleteConfirmationModal
          isOpen
          onClose={() => setDeleteProductId(null)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

export default ProductTable;
